import { makeStyles, Text, Button, shorthands, Link, Checkbox, Divider } from "@fluentui/react-components";
import * as React from "react";
import { useGlobalContext } from "../state/GlobalStateProvider";
import { useNavigate } from "react-router-dom";
import { FluentDialog } from './FluentDialog';
import { flexRowWrap, formStyle } from "../styles/GlobalStyles";
import { mobileMaxWidth, useMediaQuery } from "../util/ResponsiveDesign";
import { CenteredContent } from "./CenteredContent";

const useStyles = makeStyles({
    form: formStyle,
    flexRowWrap,
    bannerContainer: {
        ...shorthands.borderWidth(0),
        display: "flex",
        gap: "10px",
        borderRadius: "5px",
        boxSizing: "border-box",
        opacity: 1,
        position: "fixed",
        zIndex: 999999,
        margin: "1.667em",
        backgroundColor: "#333333",
        maxWidth: "1408px",
        left: 0,
        right: 0,
        bottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        boxShadow: "0 0 35px 0 rgba(0,0,0,.25)",
        padding: "2.5em 2.86em",
        flexWrap: "wrap"
    },
    bannerText: {
        flex: "1"
    },
    mobileBannerText: {
        flex: "1",
        minWidth: "100%"
    },
    bannerButton: {
        alignSelf: "flex-end"
    },
    centeredText: {
        alignSelf: "center",
        textAlign: "center",
        width: "80%"
    },
    breakText: {
        wordBreak: "break-all"
    },
    td: {
        padding: "10px 5px"
    }
});

const localStorageSettingKey = "IANUS_GUARD_COOKIE_SETTINGS";

export interface CookieConsentValue
{
    performance: boolean;
    analytics: boolean;
    advertising: boolean;
    social_networking: boolean;
    unclassified: boolean;
    essential: boolean;
    gpc: boolean;
}

export interface CookieSettings
{
    cookieConsent:
    {
        createdAt: string;
        value: CookieConsentValue;
    }
}

export interface CookieInformation
{
    name: string;
    purpose: string;
    type: string;
    expiresIn: string;
}

type CookieType = "essential" | "performance" | "analytics" | "advertising" | "social_networking" | "unclassified";
type VisibleDialog = "overview" | "none" | CookieType;


const cookies: Record<VisibleDialog, CookieInformation[]> = {
    "essential": [
        {
            name: "StaticWebAppsAuthContextCookie",
            purpose: "Microsoft Azure default cookie that is added when authenticating",
            type: "server_cookie",
            expiresIn: "5 minutes"
        },
        {
            name: "AppServiceAuthSession",
            purpose: "Microsoft Azure cookie for storing user session",
            type: "html_session_storage",
            expiresIn: "8 hours"
        },
        {
            name: "AppServiceAuthSession1",
            purpose: "Microsoft Azure cookie for storing user session",
            type: "html_session_storage",
            expiresIn: "8 hours"
        },
        {
            name: "Nonce",
            purpose: "Login related Microsoft Azure default cookie",
            type: "server_cookie",
            expiresIn: "session"
        }
    ],
    "performance": [],
    "analytics": [],
    "advertising": [],
    "social_networking": [],
    "unclassified": [],
    "overview": [],
    "none": []
};

const cookieDetailsTitle: Record<VisibleDialog, string> = {
    "essential": "Essential Cookies",
    "performance": "Performance and Functionality Cookies",
    "analytics": "Analytics and Customization Cookies",
    "advertising": "Advertising Cookies",
    "social_networking": "Social Networking Cookies",
    "unclassified": "Unclassified Cookies",
    "overview": "",
    "none": ""
}

export const CookieBanner: React.FC = () =>
{
    const styles = useStyles();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(mobileMaxWidth);
    const gpcDetected = React.useMemo(() => !!(navigator as any).globalPrivacyControl, []);

    const [visibleDialog, setVisibleDialog] = React.useState<VisibleDialog>("none")
    const [globalState, globalDispatch] = useGlobalContext();
    const [cookieSettingsUpdate, setCookieSettingsUpdate] = React.useState<CookieSettings | null>(null);

    const showBanner = React.useMemo(() =>
    {
        if (globalState.showCookieBanner)
        {
            return true;
        }

        if (globalState.cookieSettings)
        {
            return false;
        }

        const localSetting = localStorage.getItem(localStorageSettingKey);

        if (!localSetting)
        {
            return true;
        }
        else
        {
            try
            {
                const settingsJson = JSON.parse(localSetting) as CookieSettings;

                if (settingsJson?.cookieConsent)
                {
                    globalDispatch({ type: "setCookieSettings", payload: settingsJson })
                    return false;
                }
                else
                {
                    return true;
                }
            }
            catch
            {
                return true;
            }
        }
    }, [globalDispatch, globalState.cookieSettings, globalState.showCookieBanner]);

    const acceptAll = React.useCallback(() =>
    {
        const settings: CookieSettings = {
            cookieConsent: {
                createdAt: new Date().toISOString(),
                value: {
                    advertising: true,
                    analytics: true,
                    essential: true,
                    gpc: gpcDetected,
                    performance: true,
                    social_networking: true,
                    unclassified: true
                }
            }
        }

        localStorage.setItem(localStorageSettingKey, JSON.stringify(settings));
        globalDispatch({ type: "setCookieSettings", payload: settings });
    }, [globalDispatch, gpcDetected]);

    const declineAll = React.useCallback(() =>
    {
        const settings: CookieSettings = {
            cookieConsent: {
                createdAt: new Date().toISOString(),
                value: {
                    advertising: false,
                    analytics: false,
                    essential: true,
                    gpc: gpcDetected,
                    performance: false,
                    social_networking: false,
                    unclassified: false
                }
            }
        }

        localStorage.setItem(localStorageSettingKey, JSON.stringify(settings));
        globalDispatch({ type: "setCookieSettings", payload: settings });
    }, [globalDispatch, gpcDetected]);

    const openPreferences = React.useCallback(() =>
    {
        const defaultSettings: CookieSettings = {
            cookieConsent: {
                createdAt: new Date().toISOString(),
                value: {
                    advertising: false,
                    analytics: false,
                    essential: true,
                    gpc: !!(navigator as any).globalPrivacyControl,
                    performance: false,
                    social_networking: false,
                    unclassified: false
                }
            }
        };

        setCookieSettingsUpdate(globalState.cookieSettings ?? defaultSettings);
        setVisibleDialog("overview");
    }, [globalState.cookieSettings]);

    const savePreferences = React.useCallback(() =>
    {
        if (cookieSettingsUpdate != null)
        {
            localStorage.setItem(localStorageSettingKey, JSON.stringify(cookieSettingsUpdate));
            globalDispatch({ type: "setCookieSettings", payload: cookieSettingsUpdate });
            setCookieSettingsUpdate(null);
            setVisibleDialog("none");
        }
    }, [cookieSettingsUpdate, globalDispatch]);

    return (
        <>
            <FluentDialog title="Preferences" open={visibleDialog === "overview"} submitLabel="Save and Finish" onSubmit={savePreferences} hideCancel={true} onOpenChange={() => setVisibleDialog("none")} modalType="non-modal">
                <div className={styles.form}>
                    <Text>
                        We use different types of cookies to optimize your experience on our website.
                        Click on the categories below to learn more about their purposes.
                        You may choose which types of cookies to allow and can change your preferences at any time.
                        Remember that disabling cookies may affect your experience on the website.
                        You can learn more about how we use cookies by visiting our <Link onClick={() => navigate("/legal/cookiepolicy")}>Cookie Policy</Link> and <Link onClick={() => navigate("/legal/privacypolicy")}>Privacy Policy</Link>.
                    </Text>

                    { gpcDetected && (
                        <>
                            <br />
                            <Text>We detected a Global Privacy Control (GPC) signal from your browser and have disabled all non-essential cookies by default.</Text>
                        </>
                    ) }

                    <br />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox disabled checked={cookieSettingsUpdate?.cookieConsent?.value?.essential ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, essential: data.checked } }}) as CookieSettings)} label={`Essential Cookies (${cookies["essential"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("essential")}>Details</Link>
                    </div>
                    <Text>
                        These cookies are necessary to the core functionality of our website and some of its features, such as access to secure areas.
                    </Text>

                    <br />
                    <Divider />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox checked={cookieSettingsUpdate?.cookieConsent?.value?.performance ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, performance: data.checked } }}) as CookieSettings)} label={`Performance and Functionality Cookies (${cookies["performance"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("performance")}>Details</Link>
                    </div>
                    <Text>
                        These cookies are used to enhance the performance and functionality of our websites but are nonessential to their use. However, without these cookies, certain functionality (like videos) may become unavailable.
                    </Text>

                    <br />
                    <Divider />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox checked={cookieSettingsUpdate?.cookieConsent?.value?.analytics ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, analytics: data.checked } }}) as CookieSettings)} label={`Analytics and Customization Cookies (${cookies["analytics"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("analytics")}>Details</Link>
                    </div>
                    <Text>
                        These cookies collect information that can help us understand how our websites are being used. This information can also be used to measure effectiveness in our marketing campaigns or to curate a personalized site experience for you.
                    </Text>

                    <br />
                    <Divider />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox checked={cookieSettingsUpdate?.cookieConsent?.value?.advertising ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, advertising: data.checked } }}) as CookieSettings)} label={`Advertising Cookies (${cookies["advertising"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("advertising")}>Details</Link>
                    </div>
                    <Text>
                        These cookies are used to make advertising messages more relevant to you. They prevent the same ad from continuously reappearing, ensure that ads are properly displayed for advertisers, and in some cases select advertisements that are based on your interests.
                    </Text>

                    <br />
                    <Divider />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox checked={cookieSettingsUpdate?.cookieConsent?.value?.social_networking ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, social_networking: data.checked } }}) as CookieSettings)} label={`Social networking Cookies (${cookies["social_networking"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("social_networking")}>Details</Link>
                    </div>
                    <Text>
                        These cookies enable you to share our website's content through third-party social networks and other websites. These cookies may also be used for advertising purposes.
                    </Text>

                    <br />
                    <Divider />
                    <br />

                    <div className={styles.flexRowWrap}>
                        <Checkbox checked={cookieSettingsUpdate?.cookieConsent?.value?.unclassified ?? false} onChange={(_, data) => setCookieSettingsUpdate((state) => ({cookieConsent: { createdAt: state?.cookieConsent?.createdAt ?? new Date().toISOString(), value: { ...state?.cookieConsent?.value, unclassified: data.checked } }}) as CookieSettings)} label={`Unclassified Cookies (${cookies["unclassified"]?.length ?? 0})`}></Checkbox>
                        <Link as="button" onClick={() => setVisibleDialog("unclassified")}>Details</Link>
                    </div>
                    <Text>
                        These are cookies that have not yet been categorized. We are in the process of classifying these cookies with the help of their providers.
                    </Text>

                    <br />
                    <Divider />
                    <br />
                </div>
            </FluentDialog>

            <FluentDialog open={visibleDialog !== "overview" && visibleDialog !== "none" && !!visibleDialog} title={cookieDetailsTitle[visibleDialog]} hideCancel={true} submitLabel="Back" onSubmit={() => setVisibleDialog("overview")}>
                { !!cookies[visibleDialog].length && (
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Purpose</th>
                                <th>Type</th>
                                <th>Expires In</th>
                            </tr>
                        </thead>
                        <tbody>
                            { cookies[visibleDialog].map(c =>
                                {
                                    return (
                                        <tr key={c.name}>
                                            <td className={styles.td}>
                                                <Text className={styles.breakText}>{c.name}</Text>
                                            </td>
                                            <td className={styles.td}>
                                                <Text className={styles.breakText}>{c.purpose}</Text>
                                            </td>
                                            <td className={styles.td}>
                                                <Text className={styles.breakText}>{c.type}</Text>
                                            </td>
                                            <td className={styles.td}>
                                                <Text className={styles.breakText}>{c.expiresIn}</Text>
                                            </td>
                                        </tr>
                                    );
                                }
                            ) }
                        </tbody>
                    </table>
                ) }
                { !cookies[visibleDialog].length && (
                    <CenteredContent>
                        <Text className={styles.centeredText}>
                            <h3>No Cookies</h3>
                            We don't serve any cookies of this type.
                        </Text>
                    </CenteredContent>
                )}
            </FluentDialog>

            { showBanner && (
                <div className={styles.bannerContainer}>
                    <Text className={isMobile ? styles.mobileBannerText : styles.bannerText}>We use essential cookies to make our site work. With your consent, we may also use non-essential cookies to improve user experience and analyze website traffic. By clicking "Accept", you agree to our website's cookie use as described in our <Link onClick={() => navigate("/legal/cookiepolicy")}>Cookie Policy</Link>. You can change your cookie settings at any time by clicking "Preferences".</Text>
                    <Button className={styles.bannerButton} onClick={openPreferences}>Preferences</Button>
                    <Button className={styles.bannerButton} appearance="primary" onClick={declineAll}>Decline</Button>
                    <Button className={styles.bannerButton} appearance="primary" onClick={acceptAll}>Accept</Button>
                </div>
            ) }
        </>
    );
};